import React from "react";
import { MonthlyExpenseSummary } from "../../models/MonthlyExpenseSummary";
import { useMonthlyExpenseSummary } from "../../contexts/MonthlyExpenseSummaryContext";
import { Heading } from "../../design/heading";
import { Table, TableBody, TableCell, TableRow } from "../../design/table";
import { Divider } from "../../design/divider";
import moment from "moment";

interface Props {
  monthlyExpenseSummary: MonthlyExpenseSummary;
}

const MonthlyExpenseSummaryListItem: React.FC<Props> = ({
  monthlyExpenseSummary: summary,
}) => {
  const { deleteMonthlyExpenseSummaryById } = useMonthlyExpenseSummary();

  const handleDelete = async () => {
    await deleteMonthlyExpenseSummaryById(summary._id);
  };

  const format = (n?: number) => (n != null ? `$${n.toLocaleString()}` : "-");

  return (
    <div className="p-4 space-y-4 bg-white border rounded-lg shadow-md">
      <Heading>
        {moment(`${summary.year}-${summary.month}`, "YYYY-MM").format(
          "MMMM YYYY",
        )}
      </Heading>

      <Divider />

      <div className="grid grid-cols-2 text-sm gap-x-4 gap-y-2">
        <div>
          <strong>Rent / Mortgage:</strong> {format(summary.rentOrMortgage)}
        </div>
        <div>
          <strong>Property Taxes:</strong> {format(summary.propertyTaxes)}
        </div>
        <div>
          <strong>Home Insurance:</strong> {format(summary.homeInsurance)}
        </div>
        <div>
          <strong>Utilities:</strong> {format(summary.utilities)}
        </div>
        <div>
          <strong>Groceries:</strong> {format(summary.foodGroceries)}
        </div>
        <div>
          <strong>Eating Out:</strong> {format(summary.eatingOut)}
        </div>
        <div>
          <strong>Transportation:</strong> {format(summary.transportation)}
        </div>
        <div>
          <strong>Health Insurance:</strong> {format(summary.healthInsurance)}
        </div>
        <div>
          <strong>Health Care (OOP):</strong>{" "}
          {format(summary.healthCareOutOfPocket)}
        </div>
        <div>
          <strong>Child Care:</strong> {format(summary.childcare)}
        </div>
        <div>
          <strong>Education:</strong> {format(summary.education)}
        </div>
        <div>
          <strong>Clothing:</strong> {format(summary.clothing)}
        </div>
        <div>
          <strong>Laundry / Cleaning:</strong> {format(summary.laundryCleaning)}
        </div>
        <div>
          <strong>Entertainment:</strong> {format(summary.entertainment)}
        </div>
        <div>
          <strong>Other Insurance:</strong> {format(summary.insuranceOther)}
        </div>
        <div>
          <strong>Savings / Investment:</strong>{" "}
          {format(summary.savingsInvestment)}
        </div>
        <div>
          <strong>Charitable Contributions:</strong>{" "}
          {format(summary.charitableContributions)}
        </div>
        <div>
          <strong>Installment Payments:</strong>{" "}
          {format(summary.installmentPayments)}
        </div>
        <div>
          <strong>Other Expenses:</strong> {format(summary.otherExpenses)}
        </div>
      </div>
      <Divider />
      <div className="flex items-center justify-between">
        <Heading>Total:</Heading>
        <Heading>
          {format(
            Object.values(summary).reduce((acc, val) => {
              if (typeof val === "number") acc += val;
              return acc;
            }, 0),
          )}
        </Heading>
      </div>
    </div>
  );
};

export default MonthlyExpenseSummaryListItem;
